<template>
  <base-range-slider
    v-model="rangeModel"
    :min="min"
    :max="max"
    :step="0.1"
    :suffix="props.after ?? BASE_CURRENCY.symbol"
    :maxlength="props.maxlength || 10"
    compact
    @change="updateRange"
    @dragging="handleDragging"
  />
</template>

<script setup lang="ts">
import { filtersStore } from "~/store/search_filters";
import { isEqual } from "lodash";
import { BASE_CURRENCY } from "~/composables/CURRENCIES";
import eventBus from "~/utilities/composables/eventBus";

interface IProps {
  name: string;
  placeholder1?: string | undefined;
  placeholder2?: string | undefined;
  isSlider?: boolean;
  sliderMax?: number;
  sliderMin?: number;
  after?: string;
  maxlength?: number;
  converAble?: boolean;
  dropFunc: () => Function;
}

const emit = defineEmits<{ (e: "change", value: Record<string, { gte: string; lte: string }>): void }>();
const route = useRoute();
const props = defineProps<IProps>();

const useFiltersStore = filtersStore();
const min = ref(0);
const max = ref(props.sliderMax ?? 1);
const rangeModel = ref<[number, number]>([0, props.sliderMax ?? 1]);
const savedRange = ref<[number, number]>();
const isDragging = ref(false);

let timeout: ReturnType<typeof setTimeout>;

const updateRange = () => {
  isDragging.value = false;
  if (isEqual(savedRange.value, rangeModel.value)) return;
  timeout = setTimeout(() => {
    if (isDragging.value) {
      clearTimeout(timeout);
      return;
    }
    const usdRate = BASE_CURRENCY.value.usdRate;
    emit("change", {
      [props.name]: {
        gte: (rangeModel.value[0] * usdRate).toFixed(1),
        lte: (rangeModel.value[1] * usdRate).toFixed(1),
      },
    });
    savedRange.value = [rangeModel.value[0], rangeModel.value[1]];
  }, 1500);
};

const handleDragging = () => {
  isDragging.value = true;
};

const convert = (val: number | string | undefined) => {
  const baseRate = BASE_CURRENCY.value.rate;
  const v = parseFloat((val ?? 0).toString());
  if (!props.converAble) return parseFloat(v.toFixed(1));
  return parseFloat((v * baseRate).toFixed(1));
};

const dropInputRange = (nums: [number, number]) => {
  rangeModel.value = [convert(nums[0] || 0), convert(nums[1] || 100)];
  min.value = convert(nums[0] || 0);
  max.value = convert(nums[1] || 100);
};

const gatherCurrency = () => {
  nextTick(() => {
    const gteRaw =
      route.query[`${props.name}[gte]`] ??
      useFiltersStore.savedStats[`${props.name}Min`] ??
      props.sliderMin;
    const lteRaw =
      route.query[`${props.name}[lte]`] ??
      useFiltersStore.savedStats[`${props.name}Max`] ??
      props.sliderMax;
    const gte = convert(gteRaw as number | string | undefined);
    const lte = convert(lteRaw as number | string | undefined);
    rangeModel.value = [gte || 0, lte || 100];

    const locmin =
      useFiltersStore.savedStats[`${props.name}Min`] ?? props.sliderMin;
    const locmax =
      useFiltersStore.savedStats[`${props.name}Max`] ?? props.sliderMax;
    min.value = convert(locmin as number | string | undefined);
    max.value = convert(locmax as number | string | undefined);
    savedRange.value = [rangeModel.value[0], rangeModel.value[1]];
  });
};

onMounted(() => {
  const drop = props.dropFunc();
  drop(dropInputRange);
  nextTick(() => {
    gatherCurrency();
  });
  eventBus.on("change-currency", gatherCurrency);
});

onBeforeUnmount(() => {
  eventBus.off("change-currency", gatherCurrency);
});
</script>
